import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { GetJson } from '../helpers/fetchReact';
import { REST_API } from '../constants';
import { STATE_STATUS, GeoAreaTypes } from '../constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { dateRangesOverlap, epochToISOString } from '@awp/helpers/Utils';

const initialState = {
  weatherStations: [],
  areaTypeName: { value: GeoAreaTypes.CONTRACT, label: GeoAreaTypes.CONTRACT_DISPLAY },
  eetArea: null,
  contractArea: null,
  sensorType: null,
  dataRange: 0,
  endDateEpoch: null,
  weatherstationStatus: null,
  weatherStationSensorTypes: null,
  allFilteredWeatherStations: [],
  weatherStation: null,
  weatherStationId: null,
  status: STATE_STATUS.IDLE,
  error: null,
};
const hasSearch = (list, searchby) => {
  let ret = list.filter((o) => {
    return o?.value === searchby;
  });
  return ret && ret.length > 0 ? true : false;
};

export const selectAllWeatherStations = (state) => state.weatherStations.weatherStations;
export const selectWeatherStation = (state) => state.weatherStations.weatherStation;
export const selectWeatherStationId = (state) => state.weatherStations.weatherStationId;
export const selectAllFilteredWeatherStations = (state) => state.weatherStations.allFilteredWeatherStations;
export const selectWeatherStationSensorTypes = (state) => state.weatherStations.weatherStationSensorTypes;

export const getWxLoadingStatus = (state) => state.weatherStations.status;
export const selectAreaTypeName = (state) => state.weatherStations.areaTypeName;
export const selectEetArea = (state) => state.weatherStations.eetArea;
export const selectContractArea = (state) => state.weatherStations.contractArea;
export const selectSensorType = (state) => state.weatherStations.sensorType;
export const selectWeatherstationStatus = (state) => state.weatherStations.weatherstationStatus;
export const selectWeatherStationGeographicAreas = (state) => state.weatherStations.weatherStationGeographicAreas;
export const selectWeatherstationStatuses = (state) => state.weatherStations.weatherstationStatuses;

export const getWeatherStationById = createSelector(
  [(state) => state.weatherStations.weatherStations, (_, weatherStationId) => weatherStationId],
  (allWeatherStations, weatherStationId) => {
    return allWeatherStations.find((station) => station.Id === weatherStationId);
  },
);

// Selector to filter by sensor type and get the relevant weather stations
export const selectWeatherStationsBySensorType = (sensorTypeId) =>
  createSelector(
    [selectWeatherStationSensorTypes, selectAllWeatherStations],
    (weatherStationSensorTypes, weatherStations) => {
      // Step 1: Filter the sensor types by SensorTypeId and get WeatherStationIds
      const filteredWeatherStationIds = weatherStationSensorTypes
        .filter((sensorType) => sensorType.SensorTypeId === sensorTypeId)
        .map((sensorType) => sensorType.WeatherStationId);

      // Step 2: Filter the weather stations by the WeatherStationIds
      return filterWSList(weatherStations)
        .filter((ws) => {
          return !ws.isDisabled;
        })
        .filter((station) => filteredWeatherStationIds.includes(station.Id));
    },
  );

const filterWSList = (allWeatherStations) => {
  return allWeatherStations.map((ws) => {
    const _ws = { ...ws };
    _ws.isDisabled = ws.StatusTypeName !== 'Active';
    return _ws;
  });
};

const getSortedWeatherStation = createSelector(
  [(state) => state.weatherStations.weatherStations],
  (allWeatherStations) => {
    return filterWSList(allWeatherStations).sort((a, b) => {
      return a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1;
    });
  },
);

// TODO: Use selectActiveWeatherStations instead
const getActiveWeatherStation = createSelector(
  [(state) => state.weatherStations.weatherStations],
  (allWeatherStations) => {
    return filterWSList(allWeatherStations).filter((ws) => {
      return !ws.isDisabled;
    });
  },
);

export const selectActiveWeatherStations = createSelector([selectAllWeatherStations], (allWeatherStations) => {
  return allWeatherStations.filter((ws) => {
    return ws.StatusTypeName === 'Active';
  });
});

const setSelectedPrepWeatherStation = (isFullList) =>
  isFullList ? (state) => getSortedWeatherStation(state) : (state) => getActiveWeatherStation(state);
export const selectPrepWeatherStation = setSelectedPrepWeatherStation;

export const fetchWeatherStations = createAsyncThunk(
  'weatherStations/fetchWeatherStations',
  async () => {
    const url = REST_API.WEATHER_STATIONS;

    return GetJson(url).then((response) => response.json());
  },
  {
    condition: (args, { getState }) => {
      const { weatherStations: state } = getState();
      //Load weather stations are not loaded
      return state.status !== STATE_STATUS.LOADING;
    },
  },
);

export const fetchWeatherStationGeographicAreas = createAsyncThunk(
  'weatherStations/weatherStationGeographicAreas',
  async () => {
    const url = REST_API.DROPDOWNS + 'weatherstationgeographicareas';
    return GetJson(url).then((response) => response.json());
  },
);

export const fetchWeatherStationSensorTypes = createAsyncThunk(
  'weatherStations/weatherStationSensorTypes',
  async () => {
    const url = REST_API.DROPDOWNS + 'weatherstationsensortypes';
    return GetJson(url).then((response) => response.json());
  },
);

export const fetchWeatherstationStatuses = createAsyncThunk('weatherStations/watherstationStatuses', async () => {
  const url = REST_API.DROPDOWNS + 'weatherstationstatuses';
  return GetJson(url).then((response) => response.json());
});

export const weatherStationSlice = createSlice({
  name: 'weatherStations',
  initialState,
  reducers: {
    setAreaTypeName(state, action) {
      return { ...state, areaTypeName: action.payload };
    },
    setEetArea(state, action) {
      return { ...state, eetArea: action.payload };
    },
    setWeatherStation(state, action) {
      return { ...state, weatherStation: action.payload?.Id };
    },
    setWeatherStationId(state, action) {
      return { ...state, weatherStationId: action.payload };
    },
    setContractArea(state, action) {
      return { ...state, contractArea: action.payload };
    },
    setSensorType(state, action) {
      return { ...state, sensorType: action.payload };
    },
    setDataRange(state, action) {
      return { ...state, dataRange: action.payload };
    },
    setEndDateEpoch(state, action) {
      return { ...state, endDateEpoch: action.payload };
    },
    setWeatherstationStatus(state, action) {
      return { ...state, weatherstationStatus: action.payload };
    },
    setWeatherStationGeographicAreas(state, action) {
      return { ...state, weatherStationGeographicAreas: action.payload };
    },
    setAllFilteredWeatherStations(state, action) {
      const _state = { ...state };
      if (!_state) return [];

      console.debug('dataRange', _state.dataRange);
      console.debug('endDate', epochToISOString(_state.endDateEpoch));

      const wss = _state.weatherStations;
      if (!wss) return wss;
      let weatherstationStatus = _state.weatherstationStatus;
      if (!weatherstationStatus) return { ...state, allFilteredWeatherStations: wss };
      let _wss = [...wss];
      if (weatherstationStatus && isValueEmpty(weatherstationStatus.length) && _wss.length > 0) {
        //select one status
        _wss = _wss.filter((o) => {
          return o && o.StatusTypeId === weatherstationStatus.value;
        });
      } else if (
        weatherstationStatus &&
        !isValueEmpty(weatherstationStatus.length) &&
        weatherstationStatus.length > 0 &&
        _wss.length > 0
      ) {
        // multi-statuses selected

        //flatten the statuses to just the Id
        const statusTypeIds = weatherstationStatus.map((status) => status.value);

        _wss = _wss.filter((o) => statusTypeIds.includes(o?.StatusTypeId));

        /* this code is wrong, there's an easier way */
        // _wss = _wss.filter((o) => {
        //   return hasSearch(weatherstationStatus, o?.StatusTypeId);
        // });
      }
      let areaType = _state.areaTypeName;
      let area = null;
      if (areaType.value === GeoAreaTypes.CONTRACT) {
        area = _state.contractArea;
      } else if (areaType.value === GeoAreaTypes.EET) {
        area = _state.eetArea;
      }
      let wsGeoAreas = _state.weatherStationGeographicAreas;
      if (wsGeoAreas) {
        let filteredWsIds = [];
        if (areaType && (!area || area.length === 0)) {
          filteredWsIds = wsGeoAreas.filter((o) => {
            return o && o.AreaTypeName === areaType.value;
          });
        } else {
          if (area && area.length > 0) {
            filteredWsIds = wsGeoAreas.filter((o) => {
              return hasSearch(area, o?.GeographicAreaId);
            });
          }
        }
        if (filteredWsIds && filteredWsIds.length > 0) {
          _wss = _wss.filter((o) => {
            let ret = filteredWsIds.filter((f) => {
              return f?.WeatherStationId === o.Id;
            });
            return ret && ret.length > 0 ? true : false;
          });
        }
      }

      let sensorType = _state.sensorType;
      let wsSensorTypes = _state.weatherStationSensorTypes;
      if (sensorType && sensorType.label && sensorType.label !== 'All' && wsSensorTypes) {
        let filteredSensorTypeWsIds = wsSensorTypes.filter((o) => {
          return o?.SensorTypeId === sensorType.value;
        });

        if (filteredSensorTypeWsIds && filteredSensorTypeWsIds.length > 0) {
          _wss = _wss.filter((ws) => {
            let matchingSensorTypes = filteredSensorTypeWsIds.filter((st) => st?.WeatherStationId === ws.Id);

            if (matchingSensorTypes && matchingSensorTypes.length > 0) {
              if (_state.dataRange === null || _state.endDateEpoch === null) {
                return true; // No date range specified
              }
              return matchingSensorTypes.some(
                (st) =>
                  st.WeatherStationId === _state.weatherStationId || // If it's a selected weather station, include it
                  st.DateRange.some((range) =>
                    dateRangesOverlap(range.StartDate, range.EndDate, _state.endDateEpoch, _state.dataRange),
                  ),
              );
            }

            return false; // No matching sensor types found
          });
        } else {
          _wss = []; // No matching sensor types, so no weather stations
        }
      }

      return { ...state, allFilteredWeatherStations: _wss };
    },
  },
  extraReducers: {
    [fetchWeatherStations.pending]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchWeatherStations.fulfilled]: (state, action) => {
      state.status = STATE_STATUS.SUCCESS;
      state.weatherStations = action.payload;
      let _allFilteredWeatherStations = [...state.weatherStations];
      state.allFilteredWeatherStations = _allFilteredWeatherStations
        ? _allFilteredWeatherStations.filter((w) => w.StatusTypeName == 'Active')
        : [];
    },
    [fetchWeatherStations.rejected]: (state, action) => {
      state.status = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchWeatherStationGeographicAreas.pending]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchWeatherStationGeographicAreas.fulfilled]: (state, action) => {
      state.status = STATE_STATUS.SUCCESS;
      state.weatherStationGeographicAreas = action.payload;
    },
    [fetchWeatherStationGeographicAreas.rejected]: (state, action) => {
      state.status = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchWeatherStationSensorTypes.pending]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchWeatherStationSensorTypes.fulfilled]: (state, action) => {
      state.status = STATE_STATUS.SUCCESS;
      state.weatherStationSensorTypes = action.payload;
    },
    [fetchWeatherStationSensorTypes.rejected]: (state, action) => {
      state.status = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchWeatherstationStatuses.pending]: (state) => {
      state.status = STATE_STATUS.LOADING;
    },
    [fetchWeatherstationStatuses.fulfilled]: (state, action) => {
      state.status = STATE_STATUS.SUCCESS;
      state.weatherstationStatuses = action.payload;
      if (state.weatherstationStatuses && state.weatherstationStatuses.length > 0) {
        let status = state.weatherstationStatuses.filter((o) => {
          return o?.label === 'Active';
        });
        if (status && status[0]) state.weatherstationStatus = status[0];
      }
    },
    [fetchWeatherstationStatuses.rejected]: (state, action) => {
      state.status = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
  },
});
export const {
  setAreaTypeName,
  setContractArea,
  setEetArea,
  setSensorType,
  setDataRange,
  setEndDateEpoch,
  setWeatherstationStatus,
  setWeatherStationGeographicAreas,
  setAllFilteredWeatherStations,
  setWeatherStation,
  setWeatherStationId,
} = weatherStationSlice.actions;
